import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Form, useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import lodash from 'lodash';
import setLocalValue from '../utils/setLocalValue';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../utils/snackbar';
import { logEvent, signInUser } from '../utils/analytics';
import { RESET_PASSWORD_MODAL_ID } from '../modals/ResetPasswordModal';
import { IN_DEVELOPMENT } from '../constants';
import EVENTS from '../constants/events';
import redcatApiFetch from '../api';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import { syncMember } from '../thunks/syncMember';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { useTranslation } from 'react-i18next';
import StandardButton from '../components/StandardButton';
import { REGISTER_MODAL_ID } from '../modals/RegisterModal';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import FormTextField from '../components/FormTextField';
import { OrderingSelectors } from 'polygon-ordering';
import i18next from 'i18next';
import OAuthlogin from './OAuthLogin';
import ScreenLink from './ScreenLink';
import FormCheckBox from './FormCheckBox';
import { removeSavedUsername, setSavedUsername } from '../slices/savedUsername';
import type { SignupEntryPoint, SigninErrorType, RedcatAnalyticsStore } from '../analytics';
import {
  useRedcatAnalytics,
  trackSignupStarted,
  trackSigninInitiated,
  trackSigninFieldCompleted,
  trackForgotPasswordTapped,
  trackSigninFailed,
  trackSigninSucceeded,
  trackSigninAbandoned,
  trackSigninGuestSelected,
} from '../analytics';
import getRegisterProfileFields from '../selectors/getRegisterProfileFields';
import useWebOtpAutofill from '../hooks/useWebOtpAutofill';

export const SIGN_IN_MODAL_ID = 'SIGN_IN_MODAL_ID';
const { getGuestOrderingAvailable, getMember } = OrderingSelectors;

// Backend has no dedicated error_code for lockout (unlike MFA's error_code 3), so this is the
// only signal available today. See model/authinfo.py's check_on_fail in polygon-central.
const ACCOUNT_LOCKED_MESSAGE = 'Your account is temporarily locked out';

const getLoginErrorType = (e: any, data: any): SigninErrorType => {
  if (lodash.get(e, 'details.type') === 'connectivity') return 'network';
  if (data.error === ACCOUNT_LOCKED_MESSAGE) return 'account_locked';
  return 'invalid_credentials';
};

const login = (
  { email, password, reCaptchaToken, mfaToken, mfaCode }: LoginParams,
  successCallback: (result: any) => void,
  finallyCallback: () => void,
  mfaCallback: (token: string) => void,
  analytics: Pick<RedcatAnalyticsStore, 'track' | 'identify' | 'alias'>,
) => {
  let skipFinally = false;

  redcatApiFetch({
    path: '/api/v1/login',
    method: 'POST',
    body: {
      username: email,
      psw: password,
      'g-recaptcha-response': reCaptchaToken,
      tfa_pin: mfaCode,
      tfa_token: mfaToken,
      auth_type: 'M',
      save_session: '1',
      next: '/',
    },
  })
    .then((result: any) => {
      skipFinally = true;
      logEvent(EVENTS.SIGN_IN);
      successCallback(result);
      signInUser({
        id: result.data.ID,
        givenName: result.data.GivenName,
        surname: result.data.Surname,
        email: result.data.Email,
        phoneNumber: result.data.Mobile ?? result.data.Phone,
        state: result.data.State,
        city: result.data.Suburb,
        zipCode: result.data.Postcode,
        dateOfBirth: result.data.DateOfBirth,
      });
      const memberNumber = result.data.MemberNo;
      if (memberNumber) {
        analytics.alias(memberNumber);
        analytics.identify({ userId: memberNumber });
      }
      trackSigninSucceeded(analytics.track, { loginMethod: 'native' });
    })
    .catch((e: any) => {
      const data = lodash.get(e, 'details.json', {});
      if (data.error_code === 3) {
        enqueueSuccessSnackbar(i18next.t(`errorCode.${data.error_code}`));
        mfaCallback(data.additional_info);
      } else {
        logEvent(EVENTS.SIGN_IN_FAILED);
        trackSigninFailed(analytics.track, { errorType: getLoginErrorType(e, data) });
        enqueueErrorSnackbar(e);
      }
    })
    .then(() => {
      if (!skipFinally) {
        finallyCallback();
      }
    });
};

export interface SignInFormHandle {
  getCloseTrackingSnapshot: () => { fieldsCompletedCount: number; lastFieldFocused: string | null };
}

const SignInForm = React.forwardRef<
  SignInFormHandle,
  { onSuccess?: () => void; entryPoint?: SignupEntryPoint }
>(({ onSuccess, entryPoint = 'login_modal' }, ref) => {
  const [reCaptchaToken, setReCaptchaToken] = useState<null | string>(null);
  const [mfaToken, setMfaToken] = useState<null | string>(null);
  const completedFieldsRef = useRef<Set<string>>(new Set());
  const lastFieldFocusedRef = useRef<string | null>(null);
  const flowStartTimeRef = useRef<number | null>(null);
  const hasSucceededRef = useRef(false);
  // True while a Google/Facebook/Apple OAuth attempt is in flight. Opening the provider's
  // popup makes document.hidden true, which must not be mistaken for the user abandoning login.
  const oauthInProgressRef = useRef(false);
  const savedUsername = useAppSelector(state => state.savedUsername);
  const { t } = useTranslation();
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const enableReCaptcha = useAppSelector(state => state.ordering.config.enableReCaptcha);
  const reCaptchaSiteKey = useAppSelector(state => state.config.reCaptchaSiteKey);
  const member = useAppSelector(getMember);
  const registerProfileFields = useAppSelector(getRegisterProfileFields);
  const analytics = useRedcatAnalytics();
  const { track } = analytics;

  useEffect(() => {
    if (member) {
      hasSucceededRef.current = true;
      dispatch(setCurrentModal(null));
    }
  }, [member]);

  const dispatch = useAppDispatch();
  const guestOrderingEnabled = useAppSelector(getGuestOrderingAvailable);

  const schemas: SchemaType = {
    email: Yup.string().email(t('form.emailValidation')).required(t('form.passwordTooShort')),
    password: Yup.string().required(t('form.required')).min(2, t('form.passwordTooShort')),
  };

  if (mfaToken) {
    schemas.mfaCode = Yup.string().required(t('form.required')).min(4, t('form.passwordTooShort'));
  }

  const validationSchema = Yup.object().shape(schemas);

  const onSubmit = (
    {
      email,
      password,
      mfaCode,
      rememberMe,
    }: { email: string; password: string; mfaCode: string; rememberMe: boolean },
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
  ) => {
    const params: LoginParams = {
      email,
      password,
      mfaToken,
      mfaCode,
    };

    if (enableReCaptcha && reCaptchaToken) {
      params.reCaptchaToken = reCaptchaToken;
    }

    login(
      params,
      result => {
        hasSucceededRef.current = true;
        dispatch(setCurrentModal(null));
        if (IN_DEVELOPMENT && result.token) {
          setLocalValue('memberAuthToken', result.token);
        }

        dispatch(syncMember());
        if (rememberMe) {
          // Save username/email to browser
          dispatch(setSavedUsername(email));
        } else {
          dispatch(removeSavedUsername());
        }

        onSuccess?.();
      },
      () => {
        setReCaptchaToken(null);
        setSubmitting(false);
      },
      mfaToken => setMfaToken(mfaToken),
      analytics,
    );
  };
  const formik = useFormik({
    initialValues: {
      email: savedUsername ?? '',
      password: '',
      mfaCode: '',
      rememberMe: !!savedUsername,
    },
    validationSchema,
    onSubmit,
  });
  useEffect(() => {
    if (reCaptchaToken && enableReCaptcha) formik.submitForm();
  }, [reCaptchaToken, enableReCaptcha]);

  // Kept fresh every render so the mount-effect's unmount closure (which only runs once) and the
  // visibilitychange listener always see current form/analytics state, not what was captured on mount.
  const formikValuesRef = useRef(formik.values);
  formikValuesRef.current = formik.values;
  const trackRef = useRef(track);
  trackRef.current = track;

  const fireSigninAbandonedIfMidFlow = () => {
    if (hasSucceededRef.current || oauthInProgressRef.current || flowStartTimeRef.current == null) {
      return;
    }
    const values = formikValuesRef.current;
    const fieldsCompletedCount = [values.email, values.password].filter(
      value => typeof value === 'string' && value.trim().length > 0,
    ).length;
    trackSigninAbandoned(trackRef.current, {
      fieldsCompletedCount,
      timeInFlowSec: Number(((Date.now() - flowStartTimeRef.current) / 1000).toFixed(1)),
    });
    flowStartTimeRef.current = null;
  };

  useEffect(() => {
    flowStartTimeRef.current = Date.now();
    hasSucceededRef.current = false;
    oauthInProgressRef.current = false;
    return () => fireSigninAbandonedIfMidFlow();
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) fireSigninAbandonedIfMidFlow();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // OAuth logins are handled by OAuthLogin, a sibling component with its own verification
  // flow, so it can't set hasSucceededRef/oauthInProgressRef directly - these callbacks are
  // passed down so it can report into this form's abandonment tracking.
  const handleOAuthAttemptStart = useCallback(() => {
    oauthInProgressRef.current = true;
  }, []);

  const handleOAuthAttemptEnd = useCallback(() => {
    oauthInProgressRef.current = false;
  }, []);

  const handleOAuthLoginSucceeded = useCallback(() => {
    hasSucceededRef.current = true;
    oauthInProgressRef.current = false;
  }, []);

  const getIdentifierType = (value: string): 'email' | 'member_number' =>
    value.includes('@') ? 'email' : 'member_number';

  const trackFieldCompleted = (fieldKey: 'email' | 'password', fieldIndex: number) => {
    const value = formik.values[fieldKey];
    if (!value || formik.errors[fieldKey]) return;
    completedFieldsRef.current.add(fieldKey);
    const fieldName = fieldKey === 'email' ? getIdentifierType(value) : fieldKey;
    trackSigninFieldCompleted(track, { fieldName, fieldIndex });
  };

  const trackFieldFocused = (fieldName: string) => {
    lastFieldFocusedRef.current = fieldName;
  };

  useWebOtpAutofill(Boolean(mfaToken), code => formik.setFieldValue('mfaCode', code));

  useImperativeHandle(ref, () => ({
    getCloseTrackingSnapshot: () => ({
      fieldsCompletedCount: completedFieldsRef.current.size,
      lastFieldFocused: lastFieldFocusedRef.current,
    }),
  }));

  return (
    <div style={styles.innerContainer}>
      <FormikProvider value={formik}>
        <Form onSubmit={formik.handleSubmit} style={styles.form}>
          <FormTextField
            name="email"
            type="email"
            required
            label={t('field.signIn.email')}
            onChange={e => {
              setMfaToken(null);
              formik.setFieldValue('mfaCode', '');
              formik.handleChange(e);
            }}
            onFocus={() => trackFieldFocused('email')}
            handleBlur={() => trackFieldCompleted('email', 0)}
            autoFocus
          />

          <FormTextField
            name="password"
            type="password"
            required
            label={t('field.signIn.password')}
            onChange={e => {
              setMfaToken(null);
              formik.setFieldValue('mfaCode', '');
              formik.handleChange(e);
            }}
            onFocus={() => trackFieldFocused('password')}
            handleBlur={() => trackFieldCompleted('password', 1)}
          />
          {Boolean(mfaToken) && (
            <FormTextField
              id="mfaCode"
              name="mfaCode"
              type="text"
              label={t('field.signIn.mfaCode')}
              required
              autoFocus
              onChange={formik.handleChange}
              onFocus={() => trackFieldFocused('mfaCode')}
              slotProps={{
                htmlInput: {
                  autoComplete: 'one-time-code',
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                },
              }}
            />
          )}

          <FormCheckBox
            name="rememberMe"
            onChange={formik.handleChange}
            label={t(`field.signIn.rememberMe`)}
          />

          <div>
            <ScreenLink
              label={t('link.forgotPassword.prefix')}
              actionLabel={t('link.forgotPassword.action')}
              url="#"
              onClick={() => {
                trackForgotPasswordTapped(track);
                dispatch(setCurrentModal(RESET_PASSWORD_MODAL_ID));
              }}
              themeKeyLabel="forgotPasswordLinkText"
              themeKeyActionLabel="forgotPasswordLinkTextRight"
            />
          </div>

          {enableReCaptcha && !reCaptchaToken && (
            <div style={styles.captchaContainer}>
              <ReCAPTCHA
                sitekey={reCaptchaSiteKey!}
                onChange={token => setReCaptchaToken(token)}
                onErrored={() => {
                  setReCaptchaToken(null);
                  enqueueErrorSnackbar(t('problemWithReCaptchaMessage'));
                }}
                size={deviceTypeMobile ? 'compact' : 'normal'}
              />
            </div>
          )}

          <StandardButton
            label={mfaToken ? t('button.signInWithCode') : t('button.signIn')}
            onClick={() => {
              trackSigninInitiated(track, {
                rememberMe: formik.values.rememberMe,
                identifierType: getIdentifierType(formik.values.email),
              });
              formik.submitForm();
            }}
            disabled={
              !formik.isValid || formik.isSubmitting || (enableReCaptcha && !reCaptchaToken)
            }
            showSpinner={formik.isSubmitting}
            containerStyle={styles.loginButton}
            themeKey="signInSubmitButton"
          />

          <input type="submit" style={styles.hiddenSubmitButton} />
        </Form>
      </FormikProvider>

      <div style={styles.registerSection}>
        <ScreenLink
          label={t('link.register.prefix')}
          actionLabel={t('link.register.action')}
          url="#"
          onClick={() => {
            trackSignupStarted(track, {
              entryPoint,
              signupMethod: 'native',
              configuredFields: (registerProfileFields?.visible ?? []).map(field => field.id),
            });
            dispatch(setCurrentModal(REGISTER_MODAL_ID));
          }}
          themeKeyLabel="registerLinkText"
          themeKeyActionLabel="registerLinkTextRight"
        />
      </div>

      <OAuthlogin
        sourceScreen="login"
        onOAuthAttemptStart={handleOAuthAttemptStart}
        onOAuthAttemptEnd={handleOAuthAttemptEnd}
        onLoginSucceeded={handleOAuthLoginSucceeded}
      />

      {guestOrderingEnabled && (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <ScreenLink
            label={t('link.guestSignIn.prefix')}
            actionLabel={t('link.guestSignIn.action')}
            url={'#'}
            onClick={() => {
              trackSigninGuestSelected(track, { entryPoint });
              dispatch(setCurrentModal(null));
            }}
            themeKeyLabel="guestLinkText"
            themeKeyActionLabel="guestLinkTextRight"
          />
        </div>
      )}
    </div>
  );
});

export default SignInForm;

const styles: Styles = {
  // do this otherwise any children without a minSize will
  // be squashed if there isn't room (instead of overflow scroll)
  innerContainer: {
    minHeight: 'min-content',
  },
  registerSection: {
    lineHeight: 2,
    display: 'flex',
    flexDirection: 'column',
    marginTop: 10,
    marginBottom: 34,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  loginButton: {
    marginTop: 20,
    height: 40,
    borderRadius: 5,
  },

  // required to stop iOS from adding a broken "GO" button to the keyboard
  hiddenSubmitButton: {
    visibility: 'hidden',
    position: 'absolute',
  },

  captchaContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    // maxWidth: 260,
    marginTop: 15,
  },
};
